import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { FormField, Input, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { DateInput } from '../ui/date-input';
import type { CaixinhaLog } from '../../types';

function toISODate(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

function fromISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

interface LogFormProps {
  onSubmit: (data: { date: Date; value: number; description: string }) => Promise<void>;
  onClose?: () => void;
  initialData?: CaixinhaLog | null;
}

export function LogForm({ onSubmit, onClose, initialData }: LogFormProps) {
  const [dateISO, setDateISO] = useState(toISODate(initialData?.date ?? new Date()));
  const [absValue, setAbsValue] = useState(Math.abs(initialData?.value ?? 0));
  const [tipo, setTipo] = useState<'entrada' | 'saida'>(
    initialData ? (initialData.value >= 0 ? 'entrada' : 'saida') : 'entrada'
  );
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDateISO(toISODate(initialData?.date ?? new Date()));
    setAbsValue(Math.abs(initialData?.value ?? 0));
    setTipo(initialData ? (initialData.value >= 0 ? 'entrada' : 'saida') : 'entrada');
    setDescription(initialData?.description ?? '');
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dateISO || absValue <= 0) return;
    setSaving(true);
    try {
      const signedValue = tipo === 'entrada' ? absValue : -absValue;
      await onSubmit({ date: fromISODate(dateISO), value: signedValue, description: description.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormField>
        <Label>Data</Label>
        <DateInput value={dateISO} onChange={setDateISO} />
      </FormField>

      <FormField>
        <Label>Tipo</Label>
        <div className="flex gap-2">
          {(['entrada', 'saida'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTipo(t)}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                tipo === t
                  ? t === 'entrada'
                    ? 'border-success bg-success/10 text-success'
                    : 'border-destructive bg-destructive/10 text-destructive'
                  : 'border-border text-muted-foreground hover:border-foreground'
              }`}
            >
              {t === 'entrada' ? '+ Entrada' : '− Saída'}
            </button>
          ))}
        </div>
      </FormField>

      <FormField>
        <Label>
          Valor <span className="text-destructive">*</span>
        </Label>
        <CurrencyInput value={absValue} onChange={setAbsValue} />
      </FormField>

      <FormField>
        <Label>Descrição</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ex: Aporte mensal"
        />
      </FormField>

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={absValue <= 0 || saving}>
          {initialData ? 'Salvar' : 'Adicionar'}
        </Button>
      </div>
    </form>
  );
}
